import { useEffect } from 'react'

import { QuestionType } from '@factorialco/f0-react/dist/experimental'
import { CheckDouble, File, List, Pencil, Plus } from '@factorialco/f0-react/icons/app'

import i18n from 'modules/core/lib/i18n'

import { useAutoOpenChat } from 'modules/ai/hooks/useAutoOpenChat'
import { useCocreationAgent } from 'modules/ai_reports/lib/useCocreationAgent'
import { FormEditor } from 'modules/forms/components/newFormEditor/formEditor'

import { AgentStateType, QuestionnaireType } from './types'

export const FormWithAgent = ({
  initialState,
  savedState,
  reloadKey,
  setForm,
  allowedQuestionTypes,
  enabled,
}: {
  initialState: QuestionnaireType
  savedState: QuestionnaireType | undefined
  reloadKey: number
  setForm: (form: QuestionnaireType) => void
  allowedQuestionTypes?: QuestionType[]
  enabled?: boolean
}) => {
  const { state, setState } = useCocreationAgent<AgentStateType, AgentStateType>({
    name: 'co-creation-workflow',
    initialState: {
      questionnaire: initialState,
      updateForm: true,
    },
    initialMessage: i18n.t('forms.one.initial_message'),
    welcomeScreenSuggestions: [
      {
        icon: Plus,
        message: i18n.t('forms.one.welcome_suggestions.create_new_survey.message'),
        prompt: i18n.t('forms.one.welcome_suggestions.create_new_survey.prompt'),
      },
      {
        icon: Pencil,
        message: i18n.t('forms.one.welcome_suggestions.add_question.message'),
        prompt: i18n.t('forms.one.welcome_suggestions.add_question.prompt'),
      },
      {
        icon: File,
        message: i18n.t('forms.one.welcome_suggestions.create_section.message'),
        prompt: i18n.t('forms.one.welcome_suggestions.create_section.prompt'),
      },
      {
        icon: List,
        message: i18n.t('forms.one.welcome_suggestions.add_answer_options.message'),
        prompt: i18n.t('forms.one.welcome_suggestions.add_answer_options.prompt'),
      },
      {
        icon: Pencil,
        message: i18n.t('forms.one.welcome_suggestions.modify_question.message'),
        prompt: i18n.t('forms.one.welcome_suggestions.modify_question.prompt'),
      },
      {
        icon: CheckDouble,
        message: i18n.t('forms.one.welcome_suggestions.build_complete_survey.message'),
        prompt: i18n.t('forms.one.welcome_suggestions.build_complete_survey.prompt'),
      },
    ],
  })

  useAutoOpenChat()

  useEffect(() => {
    if (state?.questionnaire) {
      setForm(state.questionnaire)
    }
  }, [state, setForm])

  useEffect(() => {
    setForm(initialState)
  }, [initialState, setForm])

  useEffect(() => {
    if (savedState && reloadKey > 0) {
      setState?.({ ...state, questionnaire: savedState, updateForm: true })
    }
  }, [reloadKey])

  const handleChange = (questionnaire: QuestionnaireType) => {
    if (state) {
      setState?.({ ...state, questionnaire })
      setForm(questionnaire)
    }
  }

  if (!state) return null

  return (
    <FormEditor
      state={state.questionnaire}
      setState={handleChange}
      allowedQuestionTypes={allowedQuestionTypes}
      enabled={enabled}
    />
  )
}
